import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { generatePath, useHistory } from 'react-router';
import { Card } from 'react-bootstrap';
import './dashboard.scss';
import { getRouteByName } from 'app/routes';
import core from 'i18next';
import i18nSetup from '../../i18n';
import { useTranslation } from 'react-i18next';
import { CollectionOrderStates } from 'shared-pages';
import { useRootStore } from 'store';

export const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory<any>();
  const { appStore } = useRootStore();

  const { data: rawData } = useQuery(
    gql`
      query getDashboardIndexes {
        getDashboardIndexes {
          todayCollectionOrders
          todayExecutedCollections
          pendingCollectionOrders
          todayContainerOrders
          pendingContainerOrders
          pendingCollectionpointsRequests
        }
      }
    `,
    { fetchPolicy: 'network-only' },
  );
  const {
    todayCollectionOrders,
    todayExecutedCollections,
    pendingCollectionOrders,
    todayContainerOrders,
    pendingContainerOrders,
    pendingCollectionpointsRequests,
  } = rawData?.getDashboardIndexes ?? {};

  // Helper function to determine card color based on badge content
  const getCardColorClass = (badgeText: string): string => {
    if (badgeText.toLowerCase().includes('εκκρεμε')) {
      return 'dash-card-red'; // Red for pending items
    }
    if (badgeText.toLowerCase().includes('σήμερα')) {
      return 'dash-card-blue'; // Blue for today items
    }
    return 'dash-card-green'; // Green for everything else
  };

  enum IdxEnum {
    todayCollectionOrders,
    todayExecutedCollections,
    pendingCollectionOrders,
    todayContainerOrders,
    pendingContainerOrders,
    pendingCollectionpointsRequests,
  }
  const cardClicked = (value: IdxEnum) => {
    let loc: any;
    if (value === IdxEnum.todayCollectionOrders)
      loc = {
        pathname: generatePath(getRouteByName('Orders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingCollectionOrders)
      loc = {
        pathname: generatePath(getRouteByName('Orders')),
        //state: { filterValues: { startDate: null, endDate: new Date() } },
        // state: { filterValues: { searchText: 'admin' } },
        state: { filterValues: { collectionorderStatusId: CollectionOrderStates.NotRouted } },
      };
    else if (value === IdxEnum.todayExecutedCollections)
      loc = {
        pathname: generatePath(getRouteByName('Collections')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.todayContainerOrders)
      loc = {
        pathname: generatePath(getRouteByName('ContainerOrders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingContainerOrders)
      loc = {
        pathname: generatePath(getRouteByName('ContainerOrders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingCollectionpointsRequests)
      loc = {
        pathname: generatePath(getRouteByName('CollectionpointRequests')),
        state: { filterValues: { isApproved: 'wait' } },
      };
    else loc = { pathname: generatePath(getRouteByName('Users')), state: { filterValues: {} } };
    history.push(loc);
  };

  return (
    <div className="mt-2">
      {rawData && (
        <div className="fk-metrics-grid">
          {/* Today's Collection Orders */}
          <Card
            className={`dash-card ${getCardColorClass('Σήμερα')}`}
            onClick={() => cardClicked(IdxEnum.todayCollectionOrders)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.todayCollectionOrders)}
            aria-label={`${todayCollectionOrders} ${
              i18n.language === 'gr' ? 'Σημερινές Αιτήσεις Συλλογών' : 'Current Requests'
            } - κλικ για περισσότερα`}
          >
            <blockquote className="blockquote mb-0 card-body">
              <h1 className="outline-text">{todayCollectionOrders}</h1>
              <footer>
                {i18n.language === 'gr' ? 'Σημερινές Αιτήσεις Συλλογών' : 'Current Requests'}
              </footer>
            </blockquote>
            <span className="dash-card-badge">{i18n.language === 'gr' ? 'Σήμερα' : 'Today'}</span>
          </Card>

          {/* Pending Collection Orders */}
          <Card
            className={`dash-card ${getCardColorClass('Εκκρεμείς')}`}
            onClick={() => cardClicked(IdxEnum.pendingCollectionOrders)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.pendingCollectionOrders)}
            aria-label={`${pendingCollectionOrders} Εκκρεμείς Αιτήσεις - κλικ για περισσότερα`}
          >
            <blockquote className="blockquote mb-0 card-body">
              <h1 className="outline-text">{pendingCollectionOrders}</h1>
              <footer>
                {i18n.language === 'gr' ? 'Εκκρεμείς Αιτήσεις Συλλογών' : 'Pending Requests'}
              </footer>
            </blockquote>
            <span className="dash-card-badge">
              {i18n.language === 'gr' ? 'Εκκρεμείς' : 'Pending'}
            </span>
          </Card>

          {/* Today's Executed Collections */}
          <Card
            className={`dash-card ${getCardColorClass('Ολοκληρώθηκαν')}`}
            onClick={() => cardClicked(IdxEnum.todayExecutedCollections)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.todayExecutedCollections)}
            aria-label={`${todayExecutedCollections} ${
              i18n.language === 'gr' ? 'Σημερινές Συλλογές' : 'Current Collections'
            } - κλικ για περισσότερα`}
          >
            <blockquote className="blockquote mb-0 card-body">
              <h1 className="outline-text">{todayExecutedCollections}</h1>
              <footer>
                {i18n.language === 'gr' ? 'Σημερινές Συλλογές' : 'Current Collections'}
              </footer>
            </blockquote>
            <span className="dash-card-badge">
              {i18n.language === 'gr' ? 'Ολοκληρώθηκαν' : 'Completed'}
            </span>
          </Card>

          {/* Today's Container Orders */}
          <Card
            className={`dash-card ${getCardColorClass('Σήμερα')}`}
            onClick={() => cardClicked(IdxEnum.todayContainerOrders)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.todayContainerOrders)}
            aria-label={`${todayContainerOrders} ${
              i18n.language === 'gr'
                ? 'Σημερινές Παραγγελίες Συσκευασιών'
                : 'Current Packaging Orders'
            } - κλικ για περισσότερα`}
          >
            <blockquote className="blockquote mb-0 card-body">
              <h1 className="outline-text">{todayContainerOrders}</h1>
              <footer>
                {i18n.language === 'gr'
                  ? 'Σημερινές Παραγγελίες Συσκευασιών'
                  : 'Current Packaging Orders'}
              </footer>
            </blockquote>
            <span className="dash-card-badge">{i18n.language === 'gr' ? 'Σήμερα' : 'Today'}</span>
          </Card>

          {/* Pending Container Orders */}
          <Card
            className={`dash-card ${getCardColorClass('Εκκρεμείς')}`}
            onClick={() => cardClicked(IdxEnum.pendingContainerOrders)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.pendingContainerOrders)}
            aria-label={`${pendingContainerOrders} Εκκρεμείς Παραγγελίες Συσκευασιών - κλικ για περισσότερα`}
          >
            <blockquote className="blockquote mb-0 card-body">
              <h1 className="outline-text">{pendingContainerOrders}</h1>
              <footer>
                {i18n.language === 'gr'
                  ? 'Εκκρεμείς Παραγγελίες Συσκευασιών'
                  : 'Pending Packaging Orders'}
              </footer>
            </blockquote>
            <span className="dash-card-badge">
              {i18n.language === 'gr' ? 'Εκκρεμείς' : 'Pending'}
            </span>
          </Card>

          {/* Pending Collection Points Requests */}
          {appStore.isPlatformOwner && (
            <Card
              className={`dash-card ${getCardColorClass('Νέα')}`}
              onClick={() => cardClicked(IdxEnum.pendingCollectionpointsRequests)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === 'Enter' && cardClicked(IdxEnum.pendingCollectionpointsRequests)
              }
              aria-label={`${pendingCollectionpointsRequests} Αιτήσεις Νέων Σημείων - κλικ για περισσότερα`}
            >
              <blockquote className="blockquote mb-0 card-body">
                <h1 className="outline-text">{pendingCollectionpointsRequests}</h1>
                <footer>{t('Αιτήσεις Νέων Σημείων')}</footer>
              </blockquote>
              <span className="dash-card-badge">{i18n.language === 'gr' ? 'Νέα' : 'New'}</span>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
